/**
 * Co-Living operational read contract (Slice 1C backend, Slice 2 frontend).
 *
 * Deliberately NOT part of kpi.js. `GET /api/admin/co-living/upcoming` answers
 * two operational questions — what needs attention, and what happens next —
 * and carries no revenue, cost, occupancy or inventory figures. Those live in
 * their own contracts (`/api/admin/occupancy`, `/api/admin/dashboard/kpis`,
 * `/units/{id}/co-living-metrics`). Keeping the transport in its own domain
 * file mirrors that split instead of blurring it, and keeps kpi.js from
 * growing past the size where it would need splitting anyway (Regel 48).
 *
 * Transport and error convention only — no business logic. `days_until`,
 * successor truth, coverage counts and the horizon thresholds are all decided
 * server-side and arrive on the wire; recomputing any of them here would mean
 * two sources for one rule, which is exactly the drift this contract removes.
 *
 * Imports come from `./core` only — no cross-domain import (§5.3 Regel 4).
 */
import { API_BASE_URL, getApiHeaders, normalizeFetchError } from "./core";

const LOAD_ERROR = "Co-Living Warnungen konnten nicht geladen werden.";

/**
 * Operational warnings and upcoming events for the org's Co-Living units.
 *
 * No parameters: the organization comes from the auth context, and an
 * operational warning list is always "today" — the endpoint takes no
 * as-of date by design.
 *
 * Rejects with an Error carrying a display-ready `message`, matching the
 * convention the dashboard's other loaders already use.
 *
 * @returns {Promise<{
 *   generated_at: string,
 *   as_of: string,
 *   horizons: { termination_warning_days: number, unit_lease_start_warning_days: number, event_days: number },
 *   warnings: Array<object>,
 *   upcoming_events: Array<object>,
 * }>}
 */
export function fetchAdminCoLivingUpcoming() {
  return fetch(`${API_BASE_URL}/api/admin/co-living/upcoming`, {
    headers: getApiHeaders(),
  })
    .then((res) => {
      if (!res.ok) throw new Error(LOAD_ERROR);
      return res.json();
    })
    .catch((e) => {
      const { message } = normalizeFetchError(e, LOAD_ERROR);
      throw new Error(message || LOAD_ERROR);
    });
}
